/*
 * LICENSE PROTECTED SOFTWARE
 *
 * Bu yazilim lisans korumasi altindadir.
 * Gecerli lisans olmadan kullanilmasi yasaktir.
 *
 * Kaynak kodunun izinsiz erisimi, kopyalanmasi veya dagitimi
 * hukuki yaptirimslara tabidir.
 * (c) 2026  @cemdemirci0101
 *  bilgi@cemdemirci.com
 * This source code is proprietary and confidential.
 * Unauthorized copying, modification, or distribution
 * of this file, via any medium, is strictly prohibited.
 * (c) 2026 All Rights Reserved.
 */

import React, { useState, useEffect } from 'react';
import { usePageTitle } from '../hooks/usePageTitle';
import api, { emailTemplatesAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import DOMPurify from 'dompurify';
import { Mail, Server, Save, Send, Loader2, CheckCircle, XCircle, Shield, FileText, Eye, RotateCcw, Clock, Users } from 'lucide-react';
import { formatDateTime } from '../lib/utils';

// ========== SMTP SETTINGS TAB ==========
const SmtpTab = () => {
  const [form, setForm] = useState({ host: '', port: 587, username: '', password: '', sender_email: '', sender_name: 'Kent Konseyi', use_tls: true });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [configured, setConfigured] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [testing, setTesting] = useState(false);

  useEffect(() => { loadSettings(); }, []);

  const loadSettings = async () => {
    try {
      const r = await api.get('/smtp/settings');
      if (r.data.configured) {
        setForm({ host: r.data.host, port: r.data.port, username: r.data.username, password: '', sender_email: r.data.sender_email, sender_name: r.data.sender_name || 'Kent Konseyi', use_tls: r.data.use_tls ?? true });
        setConfigured(true);
      }
    } catch { /* silent */ }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    if (!form.host || !form.username || !form.sender_email) { toast.error('Zorunlu alanlari doldurun'); return; }
    setSaving(true);
    try { await api.put('/smtp/settings', form); toast.success('SMTP ayarlari kaydedildi'); setConfigured(true); }
    catch (e) { toast.error(e.response?.data?.detail || 'Kaydetme hatasi'); }
    finally { setSaving(false); }
  };

  const handleTest = async () => {
    if (!testEmail) { toast.error('Test e-posta adresi girin'); return; }
    setTesting(true);
    try { await api.post('/smtp/test', { to_email: testEmail }); toast.success('Test e-postasi gonderildi'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Gonderim hatasi'); }
    finally { setTesting(false); }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-slate-900 flex items-center justify-center"><Server className="w-5 h-5 text-white" /></div>
            <div>
              <CardTitle className="text-base">SMTP Yapilandirmasi</CardTitle>
              <CardDescription className="text-xs">E-posta sunucu ayarlari</CardDescription>
            </div>
            <Badge className={`ml-auto ${configured ? 'bg-green-50 text-green-700 border-green-200' : 'bg-red-50 text-red-700 border-red-200'} border text-xs`}>
              {configured ? <><CheckCircle className="w-3 h-3 mr-1" />Yapilandirildi</> : <><XCircle className="w-3 h-3 mr-1" />Yapilandirilmadi</>}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">SMTP Sunucu</Label><Input value={form.host} onChange={e => setForm(f => ({ ...f, host: e.target.value }))} placeholder="smtp.gmail.com" className="h-9 text-sm" data-testid="smtp-host" /></div>
            <div><Label className="text-xs">Port</Label><Input type="number" value={form.port} onChange={e => setForm(f => ({ ...f, port: parseInt(e.target.value) || 587 }))} className="h-9 text-sm" data-testid="smtp-port" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Kullanici Adi</Label><Input value={form.username} onChange={e => setForm(f => ({ ...f, username: e.target.value }))} className="h-9 text-sm" data-testid="smtp-user" /></div>
            <div><Label className="text-xs">Sifre</Label><Input type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} placeholder={configured ? '********' : ''} className="h-9 text-sm" data-testid="smtp-pass" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Gonderen E-posta</Label><Input value={form.sender_email} onChange={e => setForm(f => ({ ...f, sender_email: e.target.value }))} className="h-9 text-sm" data-testid="smtp-sender" /></div>
            <div><Label className="text-xs">Gonderen Adi</Label><Input value={form.sender_name} onChange={e => setForm(f => ({ ...f, sender_name: e.target.value }))} className="h-9 text-sm" /></div>
          </div>
          <div><Label className="text-xs">TLS</Label>
            <Select value={form.use_tls ? 'true' : 'false'} onValueChange={v => setForm(f => ({ ...f, use_tls: v === 'true' }))}>
              <SelectTrigger className="h-9 text-sm w-40"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="true">TLS Kullan</SelectItem><SelectItem value="false">STARTTLS</SelectItem></SelectContent>
            </Select>
          </div>
          <Button onClick={handleSave} disabled={saving} size="sm" data-testid="smtp-save">{saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}Kaydet</Button>
        </CardContent>
      </Card>

      {configured && (
        <Card>
          <CardHeader className="pb-3"><CardTitle className="text-sm">Test E-postasi</CardTitle></CardHeader>
          <CardContent>
            <div className="flex gap-2">
              <Input value={testEmail} onChange={e => setTestEmail(e.target.value)} placeholder="test@example.com" className="h-9 text-sm" data-testid="test-email" />
              <Button onClick={handleTest} disabled={testing} size="sm" data-testid="test-send">{testing ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}Gonder</Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// ========== EMAIL TEMPLATES TAB ==========
const TemplatesTab = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [editTpl, setEditTpl] = useState(null);
  const [editForm, setEditForm] = useState({ subject: '', body_html: '' });
  const [saving, setSaving] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);

  useEffect(() => { loadTemplates(); }, []);

  const loadTemplates = async () => {
    try { const r = await emailTemplatesAPI.getAll(); setTemplates(r.data || []); }
    catch { toast.error('Sablonlar yuklenemedi'); }
    finally { setLoading(false); }
  };

  const openEdit = (tpl) => {
    setEditTpl(tpl);
    setEditForm({ subject: tpl.subject, body_html: tpl.body_html });
    setEditOpen(true);
  };

  const handleSave = async () => {
    if (!editTpl) return;
    setSaving(true);
    try {
      await emailTemplatesAPI.update(editTpl.id, editForm);
      toast.success('Sablon guncellendi');
      setEditOpen(false);
      loadTemplates();
    } catch (e) { toast.error('Kaydetme hatasi'); }
    finally { setSaving(false); }
  };

  const handleReset = async (tpl) => {
    if (!window.confirm('Sablon varsayilan haline dondurulesek mi?')) return;
    try { await emailTemplatesAPI.reset(tpl.id); toast.success('Sablon sifirlandi'); loadTemplates(); }
    catch { toast.error('Sifirlama hatasi'); }
  };

  const handlePreview = async (tpl) => {
    try {
      const r = await emailTemplatesAPI.preview(tpl.id);
      setPreviewHtml(r.data.body_html);
      setPreviewOpen(true);
    } catch { toast.error('Onizleme hatasi'); }
  };

  const CODE_COLORS = { toplanti: 'bg-blue-50 text-blue-700 border-blue-200', duyuru: 'bg-amber-50 text-amber-700 border-amber-200', etkinlik: 'bg-emerald-50 text-emerald-700 border-emerald-200', anket: 'bg-violet-50 text-violet-700 border-violet-200', talep: 'bg-red-50 text-red-700 border-red-200' };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;

  return (
    <>
      <div className="space-y-3">
        {templates.map(tpl => (
          <Card key={tpl.id} className="shadow-sm" data-testid={`template-${tpl.code}`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center"><FileText className="w-4 h-4 text-slate-600" /></div>
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-semibold text-slate-900">{tpl.name}</p>
                      <Badge className={`${CODE_COLORS[tpl.code] || 'bg-slate-50 text-slate-600'} border text-[10px]`}>{tpl.code}</Badge>
                      {!tpl.is_active && <Badge variant="secondary" className="text-[10px]">Pasif</Badge>}
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5">Konu: {tpl.subject}</p>
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => handlePreview(tpl)} data-testid={`preview-${tpl.code}`}><Eye className="w-3 h-3 mr-1" />Onizle</Button>
                  <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => openEdit(tpl)} data-testid={`edit-${tpl.code}`}><FileText className="w-3 h-3 mr-1" />Duzenle</Button>
                  <Button variant="ghost" size="sm" className="h-7 text-xs text-amber-600" onClick={() => handleReset(tpl)} data-testid={`reset-${tpl.code}`}><RotateCcw className="w-3 h-3 mr-1" />Sifirla</Button>
                </div>
              </div>
              {tpl.variables && tpl.variables.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {tpl.variables.map((v, i) => (
                    <Badge key={v.key} variant="outline" className="text-[9px] font-mono">{`{{${v.key}}}`} - {v.label}</Badge>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Edit Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="template-edit-dialog">
          <DialogHeader>
            <DialogTitle className="text-base">Sablon Duzenle: {editTpl?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">E-posta Konusu</Label><Input value={editForm.subject} onChange={e => setEditForm(f => ({ ...f, subject: e.target.value }))} className="h-9 text-sm" data-testid="tpl-subject" /></div>
            <div>
              <Label className="text-xs">HTML Icerik</Label>
              <Textarea value={editForm.body_html} onChange={e => setEditForm(f => ({ ...f, body_html: e.target.value }))} rows={16} className="text-xs font-mono" data-testid="tpl-body" />
            </div>
            {editTpl?.variables && (
              <div>
                <Label className="text-xs text-slate-500">Kullanilabilir Degiskenler:</Label>
                <div className="flex flex-wrap gap-1 mt-1">
                  {editTpl.variables.map((v, i) => (
                    <Badge key={v.key} variant="outline" className="text-[10px] font-mono cursor-pointer hover:bg-blue-50"
                      onClick={() => { navigator.clipboard.writeText(`{{${v.key}}}`); toast.success(`{{${v.key}}} kopyalandi`); }}>
                      {`{{${v.key}}}`}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setEditOpen(false)}>Iptal</Button>
            <Button size="sm" onClick={handleSave} disabled={saving} data-testid="tpl-save">{saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-base">E-posta Onizleme</DialogTitle></DialogHeader>
          <div className="border rounded-lg overflow-hidden" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(previewHtml || '') }} />
        </DialogContent>
      </Dialog>
    </>
  );
};

// ========== EMAIL LOGS TAB ==========
const LogsTab = () => {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadLogs(); }, []);

  const loadLogs = async () => {
    try { const r = await emailTemplatesAPI.getLogs({ limit: 50 }); setLogs(r.data.logs || []); setTotal(r.data.total || 0); }
    catch { /* silent */ }
    finally { setLoading(false); }
  };

  const CODE_LABELS = { toplanti: 'Toplanti', duyuru: 'Duyuru', etkinlik: 'Etkinlik', anket: 'Anket', talep: 'Talep' };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;

  return (
    <Card className="shadow-sm">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2"><Clock className="w-4 h-4" />Gonderim Gecmisi ({total})</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {logs.length > 0 ? (
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50/80"><TableHead className="text-xs">Tarih</TableHead><TableHead className="text-xs">Tip</TableHead><TableHead className="text-xs">Konu</TableHead><TableHead className="text-xs">Alici</TableHead><TableHead className="text-xs">Basarili</TableHead><TableHead className="text-xs">Basarisiz</TableHead><TableHead className="text-xs">Gonderen</TableHead></TableRow>
            </TableHeader>
            <TableBody>
              {logs.map(l => (
                <TableRow key={l.id}>
                  <TableCell className="text-xs">{formatDateTime(l.created_at)}</TableCell>
                  <TableCell><Badge variant="outline" className="text-[10px]">{CODE_LABELS[l.template_code] || l.template_code}</Badge></TableCell>
                  <TableCell className="text-xs text-slate-700 max-w-[200px] truncate">{l.subject}</TableCell>
                  <TableCell className="text-xs font-medium">{l.recipient_count}</TableCell>
                  <TableCell className="text-xs text-green-600 font-medium">{l.success_count}</TableCell>
                  <TableCell className="text-xs text-red-600 font-medium">{l.fail_count}</TableCell>
                  <TableCell className="text-xs text-slate-500">{l.sent_by_name}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <div className="text-center py-12"><Mail className="w-8 h-8 text-slate-200 mx-auto mb-2" /><p className="text-sm text-slate-400">Henuz gonderim yok</p></div>
        )}
      </CardContent>
    </Card>
  );
};

// ========== MAIN PAGE ==========
const SmtpSettingsPage = () => {
  usePageTitle('E-posta Ayarlari');

  return (
    <div className="space-y-5" data-testid="smtp-settings-page">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-600 to-indigo-700 flex items-center justify-center">
          <Mail className="w-5 h-5 text-white" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-slate-900">E-posta Yonetimi</h1>
          <p className="text-xs text-slate-500">SMTP ayarlari, sablon yonetimi ve gonderim gecmisi</p>
        </div>
      </div>

      <Tabs defaultValue="templates" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="templates" className="text-xs" data-testid="tab-templates"><FileText className="w-3.5 h-3.5 mr-1.5" />Sablonlar</TabsTrigger>
          <TabsTrigger value="smtp" className="text-xs" data-testid="tab-smtp"><Server className="w-3.5 h-3.5 mr-1.5" />SMTP Ayarlari</TabsTrigger>
          <TabsTrigger value="logs" className="text-xs" data-testid="tab-logs"><Clock className="w-3.5 h-3.5 mr-1.5" />Gonderim Gecmisi</TabsTrigger>
        </TabsList>
        <TabsContent value="templates"><TemplatesTab /></TabsContent>
        <TabsContent value="smtp"><SmtpTab /></TabsContent>
        <TabsContent value="logs"><LogsTab /></TabsContent>
      </Tabs>
    </div>
  );
};

export default SmtpSettingsPage;
